import type { AppIconName } from "@/components/common/app-icon";
import type { SortDescriptor } from "@heroui/react";
import type { AppointmentTableColumnsKey } from "@/features/clinic/components/appointments/list/columns";
import type { MissingAppointmentDetail } from "@/features/clinic/helpers/department";
import type {
  OutcomeReasonAnswer,
  OutcomeReasonKey,
} from "@/types/outcome-reason";

import { AI_ASSISTANT } from "@/config/constants";
import { APPOINTMENT_STATUS_LABEL } from "@/constants/appointment-vocabulary";
import {
  APPOINTMENT_CREATOR,
  ATTENTION_QUEUE,
  CRM_FAILED_QUEUE,
  MISSING_DETAILS_QUEUE,
  OUTCOME_QUEUE,
  TO_COLLECT_QUEUE,
  UNRESOLVED_QUEUE,
  type AppointmentCreator,
  type AppointmentQueue,
} from "@/features/clinic/helpers/appointment-filter-url";
import {
  APPOINTMENT_STATUS,
  BOOKING_TYPE,
  CONSULTATION_ANSWER,
  APPOINTMENT_OUTCOME_LABEL,
  NO_SHOW_NEXT_STEP,
  RESOLUTION_STATUS,
  RESOLVE_BLOCKED_REASON,
  type AppointmentBlockedCounts,
  type AppointmentBookedBy,
  type AppointmentStatus,
  type BookingType,
  type CalendarView,
  type ConsultationAnswer,
  type ResolutionStatus,
  type ResolveBlockedReason,
} from "@/features/clinic/types/appointment";

export interface StatusConfig {
  value: AppointmentStatus;
  label: string;
  /** heroui Chip `color` tone for this status - the single source of truth
   *  shared by every status chip (list, preview, panel). */
  chipColor: "accent" | "success" | "default" | "danger" | "warning";
  dotColor: string;
  bgColor: string;
  borderColor: string;
  /** Calendar-card title color: the status hue mixed toward `--foreground`,
   *  so it tracks light/dark and always contrasts with the tinted `bgColor`. */
  textColor: string;
  /** Calendar-card secondary text (treatment, time) - a quieter mix of the
   *  same status hue so it stays in the card's color family. */
  secondaryTextColor: string;
  icon: string;
}

export const APPOINTMENT_COLUMN_ORDER: AppointmentStatus[] = [
  APPOINTMENT_STATUS.PENDING,
  APPOINTMENT_STATUS.CONFIRMED,
  APPOINTMENT_STATUS.COMPLETED,
  APPOINTMENT_STATUS.CANCELLED,
  APPOINTMENT_STATUS.NO_SHOW,
];

export const APPOINTMENT_STATUS_CONFIG: Record<
  AppointmentStatus,
  StatusConfig
> = {
  pending: {
    value: "pending",
    label: APPOINTMENT_STATUS_LABEL.PENDING,
    chipColor: "accent",
    dotColor: "var(--accent)",
    bgColor: "color-mix(in oklch, var(--accent) 15%, transparent)",
    borderColor: "var(--accent)",
    textColor: "color-mix(in oklch, var(--accent) 45%, var(--foreground))",
    secondaryTextColor: "color-mix(in oklch, var(--accent) 40%, var(--muted))",
    icon: "solar:calendar-bold",
  },
  confirmed: {
    value: "confirmed",
    label: APPOINTMENT_STATUS_LABEL.CONFIRMED,
    chipColor: "success",
    dotColor: "var(--success)",
    bgColor: "color-mix(in oklch, var(--success) 15%, transparent)",
    borderColor: "var(--success)",
    textColor: "color-mix(in oklch, var(--success) 45%, var(--foreground))",
    secondaryTextColor: "color-mix(in oklch, var(--success) 40%, var(--muted))",
    icon: "solar:check-circle-bold",
  },
  completed: {
    value: "completed",
    label: APPOINTMENT_STATUS_LABEL.COMPLETED,
    chipColor: "default",
    dotColor: "var(--muted)",
    bgColor: "color-mix(in oklch, var(--muted) 15%, transparent)",
    borderColor: "var(--muted)",
    textColor: "color-mix(in oklch, var(--muted) 30%, var(--foreground))",
    secondaryTextColor: "var(--muted)",
    icon: "solar:check-square-bold",
  },
  cancelled: {
    value: "cancelled",
    label: APPOINTMENT_STATUS_LABEL.CANCELLED,
    chipColor: "danger",
    dotColor: "var(--danger)",
    bgColor: "color-mix(in oklch, var(--danger) 15%, transparent)",
    borderColor: "var(--danger)",
    textColor: "color-mix(in oklch, var(--danger) 45%, var(--foreground))",
    secondaryTextColor: "color-mix(in oklch, var(--danger) 40%, var(--muted))",
    icon: "solar:close-circle-bold",
  },
  no_show: {
    value: "no_show",
    label: APPOINTMENT_STATUS_LABEL.NO_SHOW,
    chipColor: "warning",
    dotColor: "var(--warning)",
    bgColor: "color-mix(in oklch, var(--warning) 15%, transparent)",
    borderColor: "var(--warning)",
    textColor: "color-mix(in oklch, var(--warning) 45%, var(--foreground))",
    secondaryTextColor: "color-mix(in oklch, var(--warning) 40%, var(--muted))",
    icon: "solar:user-cross-bold",
  },
};

/**
 * Appointment statuses as `{ value, label }` options in column order, derived
 * from APPOINTMENT_STATUS_CONFIG so the (non-obvious) labels - e.g. `pending`
 * shows as "Unconfirmed" - stay defined in exactly one place. Shared with the
 * workflow builder's status dropdowns.
 */
export const APPOINTMENT_STATUS_OPTIONS: {
  value: AppointmentStatus;
  label: string;
}[] = APPOINTMENT_COLUMN_ORDER.map((status) => ({
  value: status,
  label: APPOINTMENT_STATUS_CONFIG[status].label,
}));

/**
 * A booking starts Unconfirmed or Confirmed; completed, no-show and cancelled
 * are what became of it, set once it exists. The API refuses them on create.
 */
export const APPOINTMENT_CREATE_STATUS_OPTIONS =
  APPOINTMENT_STATUS_OPTIONS.filter(
    ({ value }) =>
      value === APPOINTMENT_STATUS.PENDING ||
      value === APPOINTMENT_STATUS.CONFIRMED,
  );

export const CALENDER_VIEW_OPTIONS: { key: CalendarView; label: string }[] = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

// Blocked time keeps when and with whom; every other column is the patient's.
export const HIDDEN_APPOINTMENT_COLUMNS: ReadonlySet<AppointmentTableColumnsKey> =
  new Set(["time", "doctor", "department"]);

export const MULTI_DAY_TOAST = {
  title: "Appointments must stay within a single day",
  color: "warning",
} as const;

export const DURATION_OPTIONS = [
  { value: 15, label: "15m" },
  { value: 30, label: "30m" },
  { value: 45, label: "45m" },
  { value: 60, label: "1h" },
  { value: 90, label: "1.5h" },
  { value: 120, label: "2h" },
] as const;

export const BOOKED_BY_CONFIG: Record<
  AppointmentBookedBy,
  {
    label: string;
    icon: AppIconName;
    className: string;
  }
> = {
  user: {
    label: "Team",
    icon: "solar:user-bold",
    className: "bg-surface-secondary text-foreground",
  },
  crm: {
    label: "clinic software",
    icon: "solar:link-bold",
    className: "bg-accent/10 text-accent",
  },
  ai: {
    label: AI_ASSISTANT.name,
    // The assistant's orb, same mark the inbox and suggestions use.
    icon: AI_ASSISTANT.icon,
    className: "bg-muted text-muted",
  },
};

/**
 * The line under each answer: what it means, or what picking it does. An agent
 * should know where an answer leads before they pick it rather than after.
 */
export const CONSULTATION_ANSWER_CONSEQUENCE: Record<
  ConsultationAnswer,
  string
> = {
  booked_service: "Their next visit is on the calendar, or you'll book it now.",
  still_deciding:
    "Snoozes the conversation until the date and reminds you then.",
  nothing_booked: "The visit went fine, there's just no next step yet.",
  not_interested: "They said no. Pick why, if they told you.",
  stopped_responding: "They went quiet after the visit.",
  all_done: "That visit was everything they needed. No next visit expected.",
};

/**
 * How the visit's cost gets settled. With a CRM the ledger prices it
 * (`ledger`), or the agent says it came off an advance, was free, or is
 * chargeable and the CRM will send the figure. Without one the agent says what
 * kind of money it was and types the amount: a payment on the visit
 * (`manual`), a draw on the advance on file (`from_advance`), or a deposit
 * banked today (`new_advance`). "Free" is a real answer - it banks nothing and
 * stops the chase.
 */
export const VISIT_COST_CHOICE = {
  LEDGER: "ledger",
  ADVANCE: "advance",
  FREE: "free",
  CHARGEABLE: "chargeable",
  MANUAL: "manual",
  FROM_ADVANCE: "from_advance",
  NEW_ADVANCE: "new_advance",
} as const;

export type VisitCostChoice =
  (typeof VISIT_COST_CHOICE)[keyof typeof VISIT_COST_CHOICE];

/** The next visit's kind, on the booking step's toggle. */
export const NEXT_BOOKING_TYPE_OPTIONS: {
  value: BookingType;
  label: string;
}[] = [
  { value: BOOKING_TYPE.SERVICE, label: "Service" },
  { value: BOOKING_TYPE.CONSULTATION, label: "Consultation" },
];

/**
 * The words on the radio. Each title is the word the appointments list, the
 * funnel report, the contact filter and the workflow trigger already use for
 * that outcome, so an agent who records "Not interested" finds "Not interested"
 * everywhere else. What the answer means, or does, goes on the line under it.
 */
export const CONSULTATION_ANSWER_LABEL: Record<ConsultationAnswer, string> = {
  booked_service: "Booked",
  still_deciding: "Still deciding",
  nothing_booked: APPOINTMENT_OUTCOME_LABEL.nothing_booked,
  not_interested: APPOINTMENT_OUTCOME_LABEL.not_interested,
  stopped_responding: APPOINTMENT_OUTCOME_LABEL.stopped_responding,
  all_done: APPOINTMENT_OUTCOME_LABEL.all_done,
};

/**
 * Every value the `appointment_outcome` workflow trigger can fire with: the
 * consultation answers plus the three ways a no-show continues. Worded in the
 * report's vocabulary rather than the outcome dialog's conversational answers -
 * a workflow condition reads as a fact, not as a reply to a question.
 */
export const APPOINTMENT_OUTCOME_OPTIONS: { value: string; label: string }[] = [
  {
    value: CONSULTATION_ANSWER.BOOKED_SERVICE,
    label: APPOINTMENT_OUTCOME_LABEL.booked_service,
  },
  {
    value: CONSULTATION_ANSWER.ALL_DONE,
    label: APPOINTMENT_OUTCOME_LABEL.all_done,
  },
  {
    value: CONSULTATION_ANSWER.STILL_DECIDING,
    label: CONSULTATION_ANSWER_LABEL.still_deciding,
  },
  {
    value: CONSULTATION_ANSWER.NOTHING_BOOKED,
    label: APPOINTMENT_OUTCOME_LABEL.nothing_booked,
  },
  {
    value: CONSULTATION_ANSWER.NOT_INTERESTED,
    label: APPOINTMENT_OUTCOME_LABEL.not_interested,
  },
  {
    value: CONSULTATION_ANSWER.STOPPED_RESPONDING,
    label: APPOINTMENT_OUTCOME_LABEL.stopped_responding,
  },
  { value: NO_SHOW_NEXT_STEP.WONT_COME, label: "No-show, won't come back" },
  { value: NO_SHOW_NEXT_STEP.RESCHEDULED, label: "No-show, rebooked" },
  {
    value: NO_SHOW_NEXT_STEP.WILL_TELL_LATER,
    label: "No-show, will tell us later",
  },
];

const OUTCOME_REASON_ANSWERS: ReadonlySet<string> =
  new Set<OutcomeReasonAnswer>([
    CONSULTATION_ANSWER.NOT_INTERESTED,
    CONSULTATION_ANSWER.NOTHING_BOOKED,
    CONSULTATION_ANSWER.ALL_DONE,
    NO_SHOW_NEXT_STEP.WONT_COME,
  ]);

/** The outcomes a reason can be offered under, worded as in the report. */
export const OUTCOME_REASON_ANSWER_OPTIONS = APPOINTMENT_OUTCOME_OPTIONS.filter(
  (option) => OUTCOME_REASON_ANSWERS.has(option.value),
) as { value: OutcomeReasonAnswer; label: string }[];

/** The built-in reasons, as seeded; workflow conditions match on these keys. */
export const OUTCOME_REASON_KEY_OPTIONS: {
  value: OutcomeReasonKey;
  label: string;
}[] = [
  { value: "price_too_high", label: "Price too high" },
  { value: "nervous", label: "Nervous about the treatment" },
  { value: "bad_timing", label: "Busy / bad timing" },
  { value: "too_far", label: "Location too far" },
  { value: "went_elsewhere", label: "Went to another clinic" },
  { value: "treatment_complete", label: "Treatment complete" },
  { value: "advice_only", label: "Only needed advice" },
];

/** Mirrors the backend's cap on a reason label. */
export const OUTCOME_REASON_LABEL_MAX_LENGTH = 80;

/**
 * The worklists a nudge email can name, and what to say when one of them is
 * clear.
 *
 * Written for the front desk, not for the schema: the stored reasons are
 * `stale_status` / `payment_missing` / `waiting_on_user`, but nobody at a
 * clinic says "unresolved" - they say they do not know whether the patient
 * turned up.
 */
export const APPOINTMENT_QUEUE_SEGMENTS: {
  kind: AppointmentQueue;
  /** The page title over this worklist. Everything owed says "Catch up". */
  title: string;
  /** The line under the title. Absent means the count-based catch-up subline. */
  subline?: string;
  /**
   * Rows read as a table rather than as answer cards. The card list groups by
   * the question a row is waiting on, so a pile that owes no answer would sit
   * under a heading that is a lie about it.
   */
  isTable?: boolean;
  emptyState: { title: string; description: string };
}[] = [
  {
    kind: ATTENTION_QUEUE,
    title: "Catch up",
    emptyState: {
      title: "You are all caught up",
      description: "Nothing is waiting on an answer right now",
    },
  },
  {
    kind: UNRESOLVED_QUEUE,
    title: "Catch up",
    emptyState: {
      title: "Every visit is accounted for",
      description: "Nobody is waiting to hear whether a patient came in",
    },
  },
  {
    kind: TO_COLLECT_QUEUE,
    title: "Catch up",
    emptyState: {
      title: "Nothing left to collect",
      description: "Every finished visit has its payment recorded",
    },
  },
  {
    kind: OUTCOME_QUEUE,
    title: "Catch up",
    emptyState: {
      title: "Nobody is left waiting",
      description: "Every patient who asked for time has since been chased",
    },
  },
  {
    kind: MISSING_DETAILS_QUEUE,
    title: "Catch up",
    emptyState: {
      title: "Every upcoming booking is filled in",
      description: "Nothing is missing a treatment or a department",
    },
  },
  {
    kind: CRM_FAILED_QUEUE,
    title: "Not in the EMR",
    subline:
      "Booked here, but the EMR refused them. Open one to retry, or add the patient's MRN first.",
    isTable: true,
    emptyState: {
      title: "Every booking reached the EMR",
      description: "Nothing booked here is missing from the EMR schedule",
    },
  },
];

/** Which counter on the blocked-counts response each stored reason feeds. */
export const BLOCKED_REASON_COUNT_KEY: Record<
  ResolveBlockedReason,
  keyof AppointmentBlockedCounts
> = {
  [RESOLVE_BLOCKED_REASON.STALE_STATUS]: "staleStatus",
  [RESOLVE_BLOCKED_REASON.PAYMENT_MISSING]: "paymentMissing",
  [RESOLVE_BLOCKED_REASON.WAITING_ON_USER]: "waitingOnUser",
};

/**
 * The second axis on a worklist: who booked the rows, not what they are owed.
 *
 * "Me" is the signed-in agent rather than the team, so it answers "what is
 * mine to chase"; the assistant's tab is autopilot only, so a booking it
 * drafted and a human approved stays with that human.
 */
export const APPOINTMENT_CREATOR_SEGMENTS: {
  kind: AppointmentCreator;
  label: string;
  tooltip: string;
}[] = [
  // "Anyone", not "All": this segment sits beside the worklist's own All tab,
  // and two All pills carrying different numbers read as a contradiction.
  {
    kind: APPOINTMENT_CREATOR.ALL,
    label: "Anyone",
    tooltip: "Everything open, whoever booked it",
  },
  {
    kind: APPOINTMENT_CREATOR.ME,
    label: "Me",
    tooltip: "Only the appointments you booked yourself",
  },
  {
    kind: APPOINTMENT_CREATOR.AI,
    label: AI_ASSISTANT.name,
    tooltip: `Booked by ${AI_ASSISTANT.name} alone, with nobody approving`,
  },
];

/**
 * Said instead of the queue's own copy whenever a creator is selected: "You are
 * all caught up" under a filter reads as an all-clear the workspace has not
 * earned, and hides that a narrowing is on.
 */
export const CREATOR_SCOPED_EMPTY_STATE: Record<
  Exclude<AppointmentCreator, typeof APPOINTMENT_CREATOR.ALL>,
  { title: string; description: string }
> = {
  [APPOINTMENT_CREATOR.ME]: {
    title: "Nothing here is yours",
    description: "Switch to Anyone to see what the rest of the team still owes",
  },
  [APPOINTMENT_CREATOR.AI]: {
    title: `${AI_ASSISTANT.name} left nothing open`,
    description: "Switch to Anyone to see what was booked by a person",
  },
};

/**
 * The same three questions, plus the two answers a block could never express:
 * closed out, and not yet anybody's problem.
 *
 * `not_due` is deliberately absent rather than labelled. A booking that has not
 * happened yet owes nothing, and putting a chip on every future row to say so
 * would be chrome on the majority of the table - the quiet row is the message.
 */
export const RESOLUTION_STATUS_TAG: Partial<
  Record<ResolutionStatus, { label: string; color: StatusConfig["chipColor"] }>
> = {
  [RESOLUTION_STATUS.AWAITING_ATTENDANCE]: {
    label: "Attendance",
    color: "danger",
  },
  [RESOLUTION_STATUS.AWAITING_PAYMENT]: {
    label: "To collect",
    color: "warning",
  },
  // Not "Still deciding": that narrated the patient's state where its two
  // siblings name the clinic's, and it read as calm news when the whole reason
  // the row is here is that the time they asked for has run out. "To chase" is
  // the move, it pairs with "To collect", and it is the word this queue's own
  // empty state already uses.
  [RESOLUTION_STATUS.AWAITING_DECISION]: {
    label: "To chase",
    color: "accent",
  },
  // Muted on purpose: "done" is the resting state of a worklist, not an event.
  [RESOLUTION_STATUS.SETTLED]: { label: "Done", color: "default" },
};

/** Short enough to stay on one line in the Details column's 132px. */
export const MISSING_DETAIL_LABEL: Record<MissingAppointmentDetail, string> = {
  treatment: "No treatment",
  department: "No department",
};

/**
 * How far back a failed CRM push is still worth chasing, matching the server.
 * An older booking has been and gone, so putting it on the CRM schedule now
 * achieves nothing.
 */
export const CRM_PUSH_FAILURE_WINDOW_DAYS = 30;

/** The "not in the CRM" queue is paged by the API; the table picks a size here. */
export const CRM_FAILED_PAGE_SIZE_OPTIONS = [25, 50, 100];
export const CRM_FAILED_DEFAULT_PAGE_SIZE = 25;
/** The order the API pages in, which a paged table shows rather than re-sorts. */
export const APPOINTMENT_PAGE_SORT: SortDescriptor = {
  column: "time",
  direction: "descending",
};

/** A real answer in the booked-by picker, not a blank: nobody on the team took it. */
export const BOOKED_BY_NOBODY = "nobody";

/** The quick picks for "remind me later"; the date field takes anything else. */
export const REMIND_IN_PRESETS: { label: string; days: number }[] = [
  { label: "1 week", days: 7 },
  { label: "2 weeks", days: 14 },
  { label: "1 month", days: 30 },
];

/** Sooner than "still deciding": a patient who booked nothing cools off fast. */
export const NOTHING_BOOKED_FOLLOW_UP_PRESETS: {
  label: string;
  days: number;
}[] = [
  { label: "2 days", days: 2 },
  { label: "1 week", days: 7 },
  { label: "1 month", days: 30 },
];

/** The follow-up "still deciding" creates when the agent leaves the reminder blank. */
export const STILL_DECIDING_FOLLOW_UP_REASON = "Ask if they're ready to book";

/** How far out a recall usually lands, for a patient who was all done. */
export const RECALL_PRESETS: { label: string; days: number }[] = [
  { label: "3 months", days: 90 },
  { label: "6 months", days: 180 },
  { label: "1 year", days: 365 },
];

/** The backend's default reminder for an "all done" recall date. */
export const RECALL_FOLLOW_UP_REASON = "Time for their recall visit";

/** The backend's default reminder for a "nothing booked" follow-up date. */
export const NOTHING_BOOKED_FOLLOW_UP_REASON =
  "Check in after the consultation";

/** The follow-up a no-show who will say when they can rebook creates. */
export const REBOOK_FOLLOW_UP_REASON = "Call to rebook after the missed visit";
